// One-box player lookup (spec/dashboard.md section 11.1): the shape of a pasted value decides which exact lookups of
// `GET …/players?by=<user_id|device|nutaku|custom|name>&value=` (spec/api-conventions.md section 16.2) are asked, in order;
// the first hit wins. Pure, shared with the command palette.

export type IdKind = 'user_id' | 'nutaku' | 'device' | 'custom' | 'name';

export const ID_KINDS: readonly IdKind[] = ['user_id', 'device', 'nutaku', 'custom', 'name'];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DIGITS = /^\d+$/;
const FINGERPRINT = /^[0-9a-f]{20,}$/i;

/** The lookup order for a value: the likeliest kind first, the custom id (any shape) as a fallback. */
export function guessIdKinds(text: string): IdKind[] {
  const v = text.trim();
  if (UUID.test(v)) return ['user_id', 'custom'];
  if (DIGITS.test(v)) return ['nutaku', 'custom', 'device'];
  if (FINGERPRINT.test(v)) return ['device', 'custom'];
  return ['name', 'custom', 'device'];
}

const LABELS: Record<IdKind, string> = {
  user_id: 'user id',
  device: 'device id',
  nutaku: 'Nutaku id',
  custom: 'custom id',
  name: 'name',
};

/** The kind in words, lower case mid-sentence ("user id", "Nutaku id"). */
export function describeIdKind(kind: string): string {
  return LABELS[kind as IdKind] ?? kind;
}

export function isIdKind(kind: string): kind is IdKind {
  return (ID_KINDS as readonly string[]).includes(kind);
}
